import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Box,
  Popover,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CONTROL_STYLE, CUSTOMER_TYPES } from '../../constants/invoiceConstants';

/**
 * Component for displaying and managing customer information in the invoice
 * 
 * @param {Object} props - Component props
 * @param {string} props.customerType - Type of customer (B2B or B2C)
 * @param {Object} props.customer - Selected customer
 * @param {Object} props.agent - Selected agent
 * @param {Array} props.customers - Array of available customers
 * @param {Array} props.agents - Array of available agents
 * @param {Function} props.onCustomerTypeChange - Function to call when customer type changes
 * @param {Function} props.onCustomerChange - Function to call when customer changes
 * @param {Function} props.onAgentChange - Function to call when agent changes
 * @param {Function} props.onOpenAgentDialog - Function to call to open agent dialog
 * @param {boolean} props.hideLoyalty - Hide the Loyalty field (default: false)
 * @param {boolean} props.disableCustomerFilter - Disable customer filtering by type (default: false)
 */
const CustomerInfoSection = ({
  customerType,
  customer,
  agent,
  customers,
  agents,
  onCustomerTypeChange,
  onCustomerChange,
  onAgentChange,
  onOpenAgentDialog,
  hideLoyalty = false,
  disableCustomerFilter = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
          Customer Information
        </Typography>
        {customer && customer.id !== 0 && (
          <>
            <IconButton
              size="small"
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              sx={{
                p: 0.25,
                color: 'info.main',
                '&:hover': {
                  bgcolor: 'info.lighter',
                },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              disableRestoreFocus
              sx={{
                pointerEvents: 'none',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  pointerEvents: 'auto',
                  p: 2,
                  maxWidth: 400,
                  boxShadow: 3,
                },
                onMouseEnter: handlePopoverOpen,
                onMouseLeave: handlePopoverClose,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 1.5 }}>
                Customer Details
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Name
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {customer.name || 'N/A'}
                  </Typography>
                </Box>
                {customerType === CUSTOMER_TYPES.B2B && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        VAT Number
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {customer.vatRegistrationNumber || customer.vatNumber || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Customer Code
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {customer.code || 'N/A'}
                      </Typography>
                    </Box>
                  </>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {customer.email || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Phone
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {customer.phone || 'N/A'}
                  </Typography>
                </Box>
                {customer.address && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        Address
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {customer.address.street || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          City
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {customer.address.city || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Postal Code
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {customer.address.postalCode || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Building No.
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {customer.address.buildingNo || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Country
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {customer.address.country || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            </Popover>
          </>
        )}
      </Box>
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.25 }}>Customer Type</FormLabel>
            <RadioGroup
              row
              value={customerType}
              onChange={onCustomerTypeChange}
            >
              <FormControlLabel 
                value={CUSTOMER_TYPES.B2B} 
                control={<Radio size="small" />} 
                label={<Typography sx={{ fontSize: '0.875rem' }}>B2B</Typography>} 
              />
              <FormControlLabel 
                value={CUSTOMER_TYPES.B2C} 
                control={<Radio size="small" />} 
                label={<Typography sx={{ fontSize: '0.875rem' }}>B2C</Typography>} 
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            key={customerType}
            options={disableCustomerFilter ? customers : customers.filter(c => {
              // Filter based on customer type
              if (customerType === CUSTOMER_TYPES.B2B) {
                // For B2B, show only B2B customers (exclude Walk-in Customer)
                return c.customerType === 'B2B' && c._id !== 'walk-in' && c.name !== 'Walk In Customer';
              } else {
                // For B2C, show B2C customers AND Walk-in Customer
                return c.customerType === 'B2C' || c._id === 'walk-in' || c.name === 'Walk In Customer' || c.id === 0;
              }
            })}
            getOptionLabel={(option) => option.name}
            value={customer}
            onChange={onCustomerChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Customer *"
                margin="dense"
                size="small"
                required
                sx={CONTROL_STYLE}
              />
            )}
          />
        </Grid>
        {!hideLoyalty && (
          <Grid item xs={12}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Loyalty</InputLabel>
              <Select
                value={agent ? agent._id : ''}
                label="Loyalty"
                onChange={onAgentChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (agent) {
                          onOpenAgentDialog('edit', agent);
                        } else {
                          onOpenAgentDialog('add');
                        }
                      }} 
                      edge="end"
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 0,
                  },
                  '& .MuiSelect-select': {
                    py: 0.75,
                    fontSize: '0.875rem',
                    borderRadius: 0,
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                  <em>None</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem 
                    key={agent._id} 
                    value={agent._id}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CustomerInfoSection;
